import { IonContent, IonHeader, IonPage, IonAlert, IonItem, IonIcon, IonButton, IonGrid, IonRow, IonCol, IonAccordion, IonAccordionGroup, IonModal, IonRouterLink, useIonViewWillEnter } from '@ionic/react';
import { Storage } from '@ionic/storage';
import { useParams } from 'react-router-dom';
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import * as ReactDOM from 'react-dom'
import ListItems from '../components/ListItems';
import InnerMenu from '../components/InnerMenu';
import Map, { Marker, Popup, MapRef, Source, Layer } from 'react-map-gl';
import { Plugins, Capacitor } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import dangerZoneMonitor, { DANGER_ZONES_UPDATED_EVENT } from '../services/DangerZoneMonitorService';
import { useHistory } from 'react-router-dom';
import GpsSinglePointComponent from '../components/GpsSinglePointComponent';
import Disclaimer from "../components/Disclaimer";
import { localStore } from '../App';
import { listOutline, chevronDownOutline, key, enterOutline, locateOutline } from 'ionicons/icons'
import 'mapbox-gl/dist/mapbox-gl.css';
import PageEventHandler from "../components/PageEventHandler"
import './DocksMap.css';
import { lookAtDistance } from "../js/dockDistanceCalculator.js";
import { BASE_URL, riverSections, riverZones } from '../utils/constant';
import NavigationWarningsService from '../services/NavigationWarningsService';
import DocksService from '../services/DocksService';
import SignsService from '../services/SignsService';
import BatimetricService from '../services/BatimetricService';
import DeepnessLevelsService from '../services/DeepnessLevelsService';
import stationColorMapping from '../js/station_color_mapping.json';
import { LanguageContext } from '../contexts/LanguageContext';
import { docksMapTranslations } from '../translations/docksMap';
import NotificationRedirect from '../components/NotificationRedirect';

const navigationWarningsService = new NavigationWarningsService();
const docksService = new DocksService();
const signsService = new SignsService();
const batimetricService = new BatimetricService();
const deepnessLevelsService = new DeepnessLevelsService();

interface MapParams {
  type: 'dock' | 'warning';
  id: string;
}

interface StationMapping {
  [key: string]: {
    [key: string]: number[];
  };
}

/** Lng/lat bounds for Mapbox fitBounds: [[west, south], [east, north]] */
function boundsFromPolygonRing(ring: number[][]): [[number, number], [number, number]] | null {
  if (!ring?.length) {
    return null;
  }
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const pt of ring) {
    if (!pt || pt.length < 2) {
      continue;
    }
    const lng = Number(pt[0]);
    const lat = Number(pt[1]);
    if (Number.isNaN(lng) || Number.isNaN(lat)) {
      continue;
    }
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  if (!Number.isFinite(minLng)) {
    return null;
  }
  return [[minLng, minLat], [maxLng, maxLat]];
}

const DocksMap: React.FC = () => {
  const { language } = useContext(LanguageContext);
  const t = docksMapTranslations[language];

  const MAPBOX_TOKEN = 'pk.eyJ1IjoiYWxlLWFydCIsImEiOiJjbDFxbWxqa2wwZTNlM2xvM3M4bTEzZGpiIn0.HoRycofAyQPZ33Niv-XRtA'; // Set your mapbox token here

  const pageName = "Carta nautica digitale";
  // get query parameter
  // const { type, id } = useParams<MapParams>();
  const { id } = useParams<{ id: string; }>();
  const { type } = useParams<{ type: string; }>();
  const [showAlert2, setShowAlert2] = useState(false);
  const [docksList, setDocksList] = useState<[any] | null>(null);
  const [loadingText, setLoadingText] = useState(t.loading);
  const [selectedSignIndex, setSelectedSignIndex] = useState<number | null>(null);
  const [selectedSignTitle, setSelectedSignTitle] = useState('');
  // this will be set in GpsSinglePointComponent
  const [userCoords, setUserCoords] = useState<any | null>(null)
  const [styleLoaded, setStyleLoaded] = useState(false)
  // next 3 are being used to check/show the disclaimer
  const [showModal, setShowModal] = useState(false);
  const [disclaimerCheck, setDisclaimerCheck] = useState(false);
  const [canOpenPopup, setCanOpenPopup] = useState(true);
  const history = useHistory();
  const [thisDockDistance, setThisDockDistance] = useState(t.calculating);
  const mapRef = useRef<MapRef>(null);
  const hasAutoCenteredOnUserRef = useRef(false);

  const [bearing, setBearing] = useState(0); // Store the map bearing (rotation)
  const [userRotation, setUserRotation] = useState(0); // Store the user rotation of the map (manually set with pinch gesture)
  const [mapStyle, setMapStyle] = useState('mapbox://styles/ale-art/cl1qnhp6m00b715qglkvaxm6o'); // Track current map style
  const [is3D, setIs3D] = useState(false); // Track if 3D view is enabled

  const [warningsList, setWarningsList] = useState<[any] | null>(null);
  const [signsList, setSignsList] = useState<[any] | null>(null);
  const [batimetricList, setBatimetricList] = useState<[any] | null>(null);
  const [deepnessLevelsList, setDeepnessLevelsList] = useState<[any] | null>(null);
  const [indexToUseDock, setIndexToUseDock] = useState<number | null>(null);
  const [indexToUseWarning, setIndexToUseWarning] = useState<number | null>(null);
  const [trackUserLocation, setTrackUserLocation] = useState<boolean>(true);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const isNativePlatform = Capacitor.isNativePlatform();
  /** Zoom used when centering / re-centering on the user blue dot. */
  const USER_FOLLOW_ZOOM = 13;
  const [state, setState] = useState(() => initializeData());

  const dangerZonesGeoJson = useMemo(() => {
    if (!warningsList) {
      return null;
    }
    const features: GeoJSON.Feature[] = [];
    for (const w of warningsList as unknown as Record<string, unknown>[]) {
      const dzId = w.danger_zone_id;
      const raw = w.danger_polygon_geojson as string | undefined;
      if (!dzId || Number(dzId) <= 0 || !raw || typeof raw !== 'string') {
        continue;
      }
      try {
        const geometry = JSON.parse(raw) as GeoJSON.Geometry;
        features.push({
          type: 'Feature',
          properties: { title: (w.title as string) || '', id: dzId },
          geometry
        });
      } catch {
        /* ignore invalid geometry */
      }
    }
    if (!features.length) {
      return null;
    }
    return { type: 'FeatureCollection' as const, features };
  }, [warningsList]);

  function closePopupDock() 
  {
    console.log('closePopupDock :: current indexToUseDock: ' + indexToUseDock);
    setIndexToUseDock(null);
  }

  function closePopupWarning()
  {
    console.log('closePopupWarning :: current indexToUseWarning: ' + indexToUseWarning);
    setIndexToUseWarning(null);
  }

  function openPopupDock(index: number) 
  {
    console.log('openPopupDock :: index: ' + index + ' canOpenPopup: ' + canOpenPopup);
    setIndexToUseDock(index)
    setThisDockDistance(lookAtDistance(docksList![index], userCoords));
    // reCenterMap('dockPopup');
  }

  function openPopupWarning(index: number)
  {
    console.log('openPopupWarning :: index: ' + index + ' canOpenPopup: ' + canOpenPopup);
    setIndexToUseWarning(index)
    // reCenterMap('warningPopup');
  }

  function openSignPopup(index: number) 
  {
    console.log('openSignPopup :: styleUrl: ' + signsList![index].styleUrl);
    setSelectedSignIndex(index);
    let signName = getSignName(signsList![index]);
    setSelectedSignTitle(signName!);
    // need to handle map rotation and its effect on the signs
    // from -10 to 180 signals ok -> keep starting image
    // from -180 to -170 signals ok -> keep starting image
    // from -170 to -10 signals ko -> must be swapped
    if (userRotation > -170 && userRotation < -10) // in this case invert signals Chiamata and Rimando
    {
      if (signsList![index].styleUrl	== "#icon-ci-2")
        setSelectedSignTitle('Rimando');
      else if (signsList![index].styleUrl	== "#icon-ci-1")
        setSelectedSignTitle('Chiamata');

      else if (signsList![index].styleUrl	== "#icon-ci-11")
        setSelectedSignTitle('Direzione sinistra');
      else if (signsList![index].styleUrl	== "#icon-ci-11-sx")
        setSelectedSignTitle('Direzione destra');
    }
    else
    {
      if (signsList![index].styleUrl	== "#icon-ci-2")
        setSelectedSignTitle('Chiamata');
      else if (signsList![index].styleUrl	== "#icon-ci-1")
        setSelectedSignTitle('Rimando');

      else if (signsList![index].styleUrl	== "#icon-ci-11")
        setSelectedSignTitle('Direzione destra');
      else if (signsList![index].styleUrl	== "#icon-ci-11-sx")
        setSelectedSignTitle('Direzione sinistra');
    }
  }

  function closeSignPopup() 
  {
    setSelectedSignIndex(null);
    setSelectedSignTitle('');
  }


  function applyUserPosition(position: Position | GeolocationPosition) {
    const heading = position.coords.heading;
    if (heading !== null && heading !== undefined && !Number.isNaN(heading))
    {
      setBearing(heading);
    }
    const newCoords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
    setUserCoords(newCoords);
    dangerZoneMonitor.updatePosition(newCoords.latitude, newCoords.longitude);

    if (docksList != null && indexToUseDock != null)
    {
      setThisDockDistance(lookAtDistance(docksList[indexToUseDock], newCoords));
    }
  }

  async function refreshNativeLocation() {
    if (!isNativePlatform)
    {
      return;
    }
    try
    {
      await Geolocation.requestPermissions();
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
      applyUserPosition(position);
    }
    catch (error)
    {
      console.error('DocksMap :: refreshNativeLocation failed', error);
    }
  }

  // Seed the first GPS fix once the map style is ready (Capacitor watch owns ongoing updates).
  function activateGeolocator() {
    if (!styleLoaded && indexToUseDock == null) // in case no dock was selected
    {
      if (isNativePlatform)
      {
        void refreshNativeLocation();
      }
      setStyleLoaded(true);
    }
  }

  // called by Disclaimer child component
  function setCheckbox(value: boolean) {
    localStore.set("disclaimerCheck", true);
    setDisclaimerCheck(true);
    setShowModal(false);
  }

  // fetch the state of disclaimer checkbox - if the state was not in memory, show it again.
  async function fetchDisclaimerCheck() {
    localStore.get("disclaimerCheck")
      .then(
        (value) => {
          if (value != null || value) {
            console.log('Disclaimer accettato.');
            setDisclaimerCheck(true);
            setShowModal(false);
          }
          else {
            console.log('Disclaimer non ancora accettato.');
            setDisclaimerCheck(false);
            setShowModal(true);
          }
        },
        (error) => {
          console.log('Info sul Disclaimer non trovate.');
          setDisclaimerCheck(false);
          setShowModal(true);
        }
      );
  }
  // make sure the user checked the disclaimer and not simply quit it some other way
  function checkDisclamerCheckbox() {
    if (disclaimerCheck) // the user actually checked the box
    {
      console.log('checkbox cliccata!');
      setShowModal(false)
    }
    else // the user pressed the backbutton to avoid checking the box - send him to the home page
    {
      if (Capacitor.isNative) {
        console.log('Back cliccato!');
        if (showModal) {
          console.log('Back cliccato con la Modal presente!!');
          history.push("/home");
        }
      }
    }
  }


  // Create an async function to handle the initialization
  async function initializeData() 
  {
    // firstly check the disclaimer
    fetchDisclaimerCheck();
    
    // Wait for docks to be fetched fron server or local storage
    await fetchDocks();
    // get navigation warnings from memory or server
    await fetchWarnings();

    // get signs from memory or server
    await fetchSigns();

    // get batimetric from memory or server
    await fetchBatimetric();

    // get deepness levels from AIPO API
    await fetchDeepnessLevels();

    // run the operations depending on the url params - if any
    // console.log('initializeData - about to call runUrlParamsOperations');
    runUrlParamsOperations();    
  }

  function runUrlParamsOperations()
  {
    // if the type is dock, fetch the selected dock
    // console.log('runUrlParamsOperations :: Fetching url params - type: ' + type);
    setTrackUserLocation(false);

    // now that we have the list of docks, check if the user selected a dock
    if (type === 'dock')  
    {
      // console.log('runUrlParamsOperations :: setting dock with id: ' + id);

      // if id is a number, set the index (use + to cast to int)
      if (!isNaN(+id))
      {
        // console.log('runUrlParamsOperations :: setting dock id and array to use');
        setIndexToUseDock(parseInt(id));
      }
    }
    else if (type === 'warning') 
    {
      // console.log('runUrlParamsOperations :: setting warning with id: ' + id);

      // if warningId is present, fetch the warning
      if (!isNaN(+id))
      {
        // console.log('runUrlParamsOperations :: setting warning id and array to use');
        setIndexToUseWarning(parseInt(id));
      }
    }
    else
    {
      setTrackUserLocation(true);
    }
  }
      
  useIonViewWillEnter(() => {
    hasAutoCenteredOnUserRef.current = false;
    void refreshNativeLocation();
  });

  // When DangerZoneMonitorService detects a zone-list change (every ~60s fetch), refresh polygons on the map.
  useEffect(() => {
    const onDangerZonesUpdated = () => {
      void refreshWarningsForMap();
    };
    window.addEventListener(DANGER_ZONES_UPDATED_EVENT, onDangerZonesUpdated);
    return () => {
      window.removeEventListener(DANGER_ZONES_UPDATED_EVENT, onDangerZonesUpdated);
    };
  }, []);

  // Keep the camera on the blue dot while follow mode is on. Capacitor (native)
  // / browser watchPosition own GPS — Mapbox GeolocateControl is not used.
  useEffect(() => {
    if (
      !userCoords ||
      !trackUserLocation ||
      indexToUseDock != null ||
      !mapRef.current
    )
    {
      return;
    }

    const center: [number, number] = [userCoords.longitude, userCoords.latitude];

    if (!hasAutoCenteredOnUserRef.current)
    {
      hasAutoCenteredOnUserRef.current = true;
      mapRef.current.flyTo({
        center,
        zoom: USER_FOLLOW_ZOOM,
        duration: 800,
      });
      return;
    }

    mapRef.current.easeTo({
      center,
      duration: 500,
    });
  }, [userCoords, trackUserLocation, indexToUseDock]);

  function handleMapDragStart() {
    if (trackUserLocation)
    {
      // User took control → stop following until they tap re-center.
      setTrackUserLocation(false);
    }
  }

  /** Top-right locate button: re-center on the blue dot at default zoom and resume follow. */
  function handleRecenterOnUser() {
    setTrackUserLocation(true);
    if (userCoords && mapRef.current)
    {
      hasAutoCenteredOnUserRef.current = true;
      mapRef.current.flyTo({
        center: [userCoords.longitude, userCoords.latitude],
        zoom: USER_FOLLOW_ZOOM,
        duration: 800,
      });
    }
    else
    {
      // No fix yet — next position update will fly with default zoom.
      hasAutoCenteredOnUserRef.current = false;
    }
    void refreshNativeLocation();
  }

  useEffect(() => {
    let webWatchId: number | null = null;
    let nativeWatchId: string | null = null;
    let cancelled = false;

    const startWatch = async () => {
      if (isNativePlatform)
      {
        try
        {
          await Geolocation.requestPermissions();
          nativeWatchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true },
            (position, err) => {
              if (cancelled)
              {
                return;
              }
              if (err)
              {
                console.error('DocksMap :: Geolocation watch error', err);
                return;
              }
              if (position)
              {
                applyUserPosition(position);
              }
            },
          );
        }
        catch (error)
        {
          console.error('DocksMap :: failed to start native location watch', error);
        }
        return;
      }

      if (!navigator.geolocation)
      {
        console.warn('DocksMap :: navigator.geolocation not available');
        return;
      }

      webWatchId = navigator.geolocation.watchPosition(
        (position) => applyUserPosition(position),
        (error) => {
          console.error('DocksMap :: web geolocation error', error);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000,
        },
      );
    };

    void startWatch();

    return () => {
      cancelled = true;
      if (webWatchId != null)
      {
        navigator.geolocation.clearWatch(webWatchId);
      }
      if (nativeWatchId != null)
      {
        void Geolocation.clearWatch({ id: nativeWatchId });
      }
    };
  }, []);

  useEffect(() => {
    runUrlParamsOperations();

    // console.log('useEffect 3 :: indexToUseDock: ' + indexToUseDock + ' indexToUseWarning: ' + indexToUseWarning + 'about to call reCenterMap(list)');

    reCenterMap('lists');
  }, [isMapLoaded, type, id]);


  function reCenterMap(caller: string)
  {
    // console.log('reCenterMap :: caller: ' + caller);

    let lat = 0;
    let lng = 0;

    if (caller === 'lists' && type === 'dock' && id !== null && docksList !== null)
    {
      lat = docksList![parseInt(id)].lat;
      lng = docksList![parseInt(id)].lng;
    }
    else if (caller === 'lists' && type === 'warning' && id !== null && warningsList !== null)
    {
      const w = warningsList[parseInt(id, 10)] as unknown as Record<string, unknown>;
      const map = mapRef.current?.getMap();
      const dzId = w.danger_zone_id;
      const raw = w.danger_polygon_geojson;
      if (Number(dzId) > 0 && typeof raw === 'string' && map) {
        try {
          const geom = JSON.parse(raw) as { type?: string; coordinates?: number[][][] };
          if (geom.type === 'Polygon' && geom.coordinates?.[0]) {
            const b = boundsFromPolygonRing(geom.coordinates[0]);
            if (b) {
              map.fitBounds(b, { padding: 72, duration: 1000, maxZoom: 15 });
              return;
            }
          }
        } catch {
          /* fall through to center point */
        }
      }
      lat = Number(w.lat);
      lng = Number(w.lng);
    }

    // console.log('reCenterMap :: lat: ' + lat + ' lng: ' + lng);
    if (lat !== 0 && lng !== 0)
    {
      mapRef.current!.flyTo({
        center: [lng, lat],
        zoom: 16,
        duration: 1000  // animation duration in milliseconds
      });
    }
  }

  const handleMapLoad = () => {
    if (mapRef.current) 
    {
      mapRef.current.getMap()?.resize();
      setIsMapLoaded(true); // Set map loaded state to true
    }
  };

  // Toggle between satellite and streets map styles
  const toggleMapStyle = () => {
    setMapStyle((prevStyle) =>
      prevStyle === 'mapbox://styles/mapbox/streets-v11'
        ? 'mapbox://styles/ale-art/cl1qnhp6m00b715qglkvaxm6o'
        : 'mapbox://styles/mapbox/streets-v11'
    );
  };

  const handleMapZoom = (zoom: number) => {
    // Define zoom level thresholds and their corresponding marker classes
    const zoomLevels = [
      { level: 8, class: 'visible-marker-15' },
      { level: 9, class: 'visible-marker-5' },
      { level: 10, class: 'visible-marker-3' }, 
      { level: 11, class: 'visible-marker-2' },
      { level: 12, class: 'visible-marker-other' }
    ];

    // Handle visibility for each marker class based on zoom level
    zoomLevels.forEach(({ level, class: markerClass }) => {
      const display = zoom > level ? 'block' : 'none';
      Array.from(document.getElementsByClassName(markerClass))
        .forEach((element: any) => {
          element.style.display = display;
        });
    });

    // Handle batimetric name labels
    const batimetricElements = Array.from(document.getElementsByClassName('batimetric-name'));
    batimetricElements.forEach((element: any) => {
      element.style.display = zoom > 11 ? 'block !important' : 'none';
    });
  }

 // Update the map handler to handle manual map rotation
  const handleRotationChange = (newRotation: number) => 
  {
    setUserRotation(newRotation);

    // need to handle map rotation and its effect on the signs
    // from -20 to 180 signals ok -> keep starting image
    // from -180 to -160 signals ok -> keep starting image
    // from -160 to -20 signals ko -> must be swapped
    if (selectedSignIndex)
    {
      if (userRotation > -170 && userRotation < -10) // in this case invert signals Chiamata and Rimando
      {
        if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-2")
          setSelectedSignTitle('Rimando');
        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-1")
          setSelectedSignTitle('Chiamata');

        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-11")
          setSelectedSignTitle('Direzione sinistra');
        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-11-sx")
          setSelectedSignTitle('Direzione destra');
      }
      else
      {
        if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-2")
          setSelectedSignTitle('Chiamata');
        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-1")
          setSelectedSignTitle('Rimando');
        
        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-11")
          setSelectedSignTitle('Direzione destra');
        else if (signsList![selectedSignIndex].styleUrl	== "#icon-ci-11-sx")
          setSelectedSignTitle('Direzione sinistra');
      }
    }


    // invert sign icon for direction signs
    const signIcons = document.querySelectorAll('.direction-icons');
    if (signIcons) 
    {
      if (userRotation > -170 && userRotation < -10) // in this case invert signals Direzione dx and sx
      {
        signIcons.forEach((signIcon) => {
            const currentSrc = signIcon.getAttribute('icon');
            if (currentSrc === `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11-sx.svg`) 
            {
              signIcon.setAttribute('icon', `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11.svg`);
            } 
            else if (currentSrc === `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11.svg`) 
            {
              signIcon.setAttribute('icon', `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11-sx.svg`);
            }
        });
      }      
      else
      {
        signIcons.forEach((signIcon) => {
            const currentSrc = signIcon.getAttribute('icon');
            if (currentSrc === `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11-sx.svg`) 
            {
              signIcon.setAttribute('icon', `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11.svg`);
            } 
            else if (currentSrc === `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11.svg`) 
            {
              signIcon.setAttribute('icon', `${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11-sx.svg`);
            }
        });
      }
    }
  }

  // fetch docks from memory or server
  async function fetchDocks() 
  {
    if (docksList == null) 
    {
      try 
      {
        const docks = await docksService.fetchDocks();
        if (docks) 
        {
          setDocksList(docks);
        } 
        else 
        {
          setLoadingText(t.noDocksMemory);
          setShowAlert2(true);
        }
      } 
      catch (error) 
      {
        setShowAlert2(true);
      }
    }
  }

  async function fetchWarnings() 
  {
    if (warningsList == null) 
    {
      try 
      {
        const openWarningDeepLink = type === 'warning' && id != null && id !== '';
        const warnings = openWarningDeepLink
          ? await navigationWarningsService.refreshWarningsFromServer()
          : await navigationWarningsService.fetchWarnings();
        if (warnings) 
        {
          setWarningsList(warnings);
        } 
        else 
        {
          setLoadingText(t.noWarningsMemory);
          setShowAlert2(true);
        }
      } 
      catch (error) 
      {
        setShowAlert2(true);
      }
    }
  }

  /** Force-refresh warnings from server so danger-zone polygons on the map stay current. */
  async function refreshWarningsForMap()
  {
    try
    {
      const warnings = await navigationWarningsService.refreshWarningsFromServer();
      if (warnings)
      {
        setWarningsList(warnings);
        console.log('DocksMap :: warnings/danger zones refreshed after monitor update');
      }
    }
    catch (error)
    {
      console.warn('DocksMap :: could not refresh warnings after danger-zone update', error);
    }
  }

  async function fetchSigns() 
  {
    if (signsList == null) 
    {
      try 
      {
        const signs = await signsService.fetchSigns();
        if (signs) 
        {
          setSignsList(signs);
        } 
        else 
        {
          setLoadingText(t.noSignsMemory);
          setShowAlert2(true);
        }
      } 
      catch (error) 
      {
        setShowAlert2(true);
      }
    }
  }

  async function fetchBatimetric() 
  {
    if (batimetricList == null) 
    {
      try 
      {
        const batimetric = await batimetricService.fetchBatimetric();
        if (batimetric) 
        {
          setBatimetricList(batimetric);
          console.log('fetchBatimetric :: numero misuratori batimetrici: ' + batimetric.length);

          // get batimetric data from AIPO website and add them onsreen
          // [better to call it after retrieving the other batimetricdata from the server so that we have necessary references when populating the map]
          await fetchBatimetricWebSiteData(batimetric);
        } 
        else 
        {
          setLoadingText(t.noBathymetricMemory);
          setShowAlert2(true);
        }
      } 
      catch (error) 
      {
        setShowAlert2(true);
      }
    }
  }

  async function fetchBatimetricWebSiteData(batimetricList: any)
  {
    try 
    {
      console.log('fetchBatimetricWebSiteData :: fetching data from AIPO website');
        // Fetch data from your server-side proxy
        const url = `${BASE_URL}/poServer.php?act=14&h=gkzLoFk0MN58TGqCJj`;
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        // do the html parsing
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const table = doc.querySelector('.table_bollettino');
        if (table) 
        {
          console.log('fetchBatimetricWebSiteData :: table found');
          // turn the data into an array out of couples location_name/deepness_measure and add it to each batimetricList object
          parseTableData(table, batimetricList);
        }
        else 
        {
          setLoadingText(t.noBathymetricMemory);
          setShowAlert2(true);
        }
    } 
    catch (error:any) 
    {
        setShowAlert2(true);
    }
  };

  async function fetchDeepnessLevels()
  {
    const deepnessLevels = await deepnessLevelsService.fetchLevels();
    if (deepnessLevels)
    {
      setDeepnessLevelsList(deepnessLevels);
    }
    else 
    {
      setLoadingText(t.noBathymetricMemory);
      setShowAlert2(true);
    }
  }

// Function to parse the HTML table and extract location and depth
function parseTableData(table: any, batimetricList: any) 
{
  const rows = table.querySelectorAll('tr');
  console.log("parseTableData :: dati batimetrici scaricati dal sito di AIPO - totale: " + rows.length+" valori");
  const data: { location: any; depth: any; }[] = [];

  rows.forEach((row: any) => {
      // Skip header rows
      if (row.classList.contains('title-1') || row.classList.contains('subtitle-1')) return;

      const cells = row.querySelectorAll('td');
      if (cells.length === 2) 
      {
        const location = cells[0].textContent.trim();
        const depth = cells[1].textContent.trim();
        // console.log('parseTableData :: location: ' + location + ' depth: ' + depth);
        
        // here i could loop trough the batimetricList and match the location with the one in the table
        // and then add the depth to the batimetricList
        if (batimetricList)
        {
          batimetricList.forEach((batimetric: any) => {
            if (batimetric.extendedData_Nome_Local == location)
            {
              batimetric.p = depth;
              // console.log('parseTableData :: got a match! name: ' + batimetric.extendedData_Nome_Local + ' depth: ' + batimetric.p);
            }
          });
        }

        // Add to the data array, including "NP" rows
        data.push({ location, depth });
      }
  });

  return data;
}

  // Toggle between 2D and 3D view
  const toggle3DView = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();

      if (is3D) {
        // Disable 3D: Reset pitch, remove terrain and 3D buildings
        map.setPitch(0);
        map.setTerrain(null);

        const style = map.getStyle();
        if (style && style.layers) {
          map.setLayoutProperty('3d-buildings', 'visibility', 'none');
        }
      } else {
        // Enable 3D: Set pitch, add terrain and 3D buildings
        map.setPitch(60); // Increase pitch to give a 3D perspective
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 }); // Add terrain source with some exaggeration

        const style = map.getStyle();
        if (style && style.layers) {
          if (!map.getLayer('3d-buildings')) {
            map.addLayer({
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': [
                  'interpolate', ['linear'], ['zoom'],
                  15, 0,
                  15.05, ['get', 'height']
                ],
                'fill-extrusion-base': [
                  'interpolate', ['linear'], ['zoom'],
                  15, 0,
                  15.05, ['get', 'min_height']
                ],
                'fill-extrusion-opacity': 0.6
              }
            });
          } else {
            map.setLayoutProperty('3d-buildings', 'visibility', 'visible');
          }
        }
      }
      setIs3D(!is3D); // Toggle 3D state
    }
  };

  function inferSignRiverSection(sign: any)
  {
    // check if the sign is on a river section
    const riverSection = riverSections.find(section => {
      // Check if point is within longitude bounds  
      const withinLng = (section.lng_start <= section.lng_end)
        ? (sign.point_lng >= section.lng_start && sign.point_lng <= section.lng_end) 
        : (sign.point_lng >= section.lng_end && sign.point_lng <= section.lng_start);

      return withinLng;
    });

    if (riverSection)
    {
      return riverSection.name;
    }    
  }

  function inferSignRiverZone(sign: any)
  {
    // check if the sign is on a river zone
    const riverZone = riverZones.find(zone => {
      // Check if point is within longitude bounds  
      const withinLng = (zone.lng_start <= zone.lng_end)
        ? (sign.point_lng >= zone.lng_start && sign.point_lng <= zone.lng_end) 
        : (sign.point_lng >= zone.lng_end && sign.point_lng <= zone.lng_start);

      return withinLng;
    });

    if (riverZone)
    {
      return riverZone.name;
    }
  }

  function getSignName(sign: any)
  {
    if (sign.styleUrl	== "#icon-ci-1")
      return "Rimando";
    else if (sign.styleUrl	== "#icon-ci-2")
      return "Chiamata";
    else if (sign.styleUrl	== "#icon-ci-3")
      return "Prosecuzione";
    else if (sign.styleUrl	== "#icon-ci-4")
      return "Tocca scappa";
    else if (sign.styleUrl	== "#icon-ci-5")
      return "Campata navigabile";
    else if (sign.styleUrl	== "#icon-ci-6")
      return "Navigare con prudenza";
    else if (sign.styleUrl	== "#icon-ci-8")
      return "Boa bianca";
    else if (sign.styleUrl	== "#icon-ci-9-2")
      return "Ostacoli";
    else if (sign.styleUrl	== "#icon-ci-10")
      return "Boa rossa";
    else if (sign.styleUrl	== "#icon-ci-11")
      return "Direzione destra";
    else if (sign.styleUrl	== "#icon-ci-11-sx")
      return "Direzione sinistra";
    else if (sign.styleUrl	== "#icon-ci-12")
      return "Divieto transito";
    else if (sign.styleUrl	== "#icon-ci-13")
      return "Obbligo fermata";    
    else if (sign.styleUrl	== "#icon-ci-14")
      return "Boa bianca Virtuale";
    else if (sign.styleUrl	== "#icon-ci-15")
      return "Boa rossa Virtuale";
    else return "";
  }

  function getSignIcon(sign: any)
  {
    // console.log('getSignIcon :: sign: ' + sign.styleUrl);
    if (sign.styleUrl	== "#icon-ci-1")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/rimando-icon-ci-1.svg`} className="dock-sign-icon rimando"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-2")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/chiamata-icon-ci-2.svg`} className="dock-sign-icon chiamata"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-3")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/prosecuzione-icon-ci-3.svg`} className="dock-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-4")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/tocca-scappa-icon-ci-4.svg`} className="dock-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-5")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/campata-navigabile-icon-ci-5.svg`} className="dock-sign-icon-small"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-6")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/navigare-prudenza-icon-ci-6.svg`} className="dock-sign-icon"></IonIcon>;
    // else if (sign.styleUrl	== "#icon-ci-7") // not present in the list
    //   return <IonIcon icon={} className="dock-legend-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-8")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/boa-bianca-icon-ci-8.svg`} className="buoy-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-9-2")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/ostacoli-icon-ci-9-3.svg`} className="dock-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-10")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/boa-rossa-icon-ci-10.svg`} className="buoy-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-11")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11.svg`} className="dock-sign-icon direction-icons"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-11-sx")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/direzione-icon-ci-11-sx.svg`} className="dock-sign-icon direction-icons"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-12")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/divieto-transito-icon-ci-14.svg`} className="dock-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-13")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/obbligo-fermata-icon-ci-13.svg`} className="dock-sign-icon"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-14")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/boa-bianca-icon-ci-8.svg`} className="buoy-sign-icon-blink"></IonIcon>;
    else if (sign.styleUrl	== "#icon-ci-15")
      return <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/boa-rossa-icon-ci-10.svg`} className="buoy-sign-icon-blink"></IonIcon>;
  }

  return (
    <IonPage>
      <IonHeader>
        <InnerMenu name={t.pageTitle} />
      </IonHeader>
      <IonContent fullscreen>
        <IonItem lines="none">
          <h1 className="mainTit" slot="start">{t.pageTitle}</h1>
          {/* commented for now as redundant
          <IonButton slot="end" routerLink="/docks" fill="clear" size="large">
            <IonIcon icon={listOutline}></IonIcon>
          </IonButton>
          */}

          <IonButton slot="end" onClick={toggleMapStyle}>
            {mapStyle === 'mapbox://styles/mapbox/streets-v11' ? t.satellite : t.street}
            {/* improve button style as it doesnt show up on mobile */}
          </IonButton>
          <IonButton slot="end" onClick={toggle3DView}>
            {is3D ? '2D' : '3D'}
          </IonButton>
        </IonItem>
        <IonAccordionGroup>
          <IonAccordion value="legend">
            <IonItem slot="header">
              <h2>{t.legend}</h2>
            </IonItem>
            <IonGrid slot="content">
              {/* <IonRow>
                <IonCol key={1} size="2">
                  <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/line.svg`} className="dock-legend-icon"></IonIcon>
                </IonCol>
                <IonCol key={2} size="10">
                  Idrovia navigabile
                </IonCol>
              </IonRow> */}
              <IonRow>
                <IonCol key={1} size="2" style={{textAlign: 'center'}}>
                  <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/map-marker.svg`} className="dock-legend-icon"></IonIcon>
                </IonCol>
                <IonCol key={2} size="10">
                  {t.docks}
                </IonCol>
              </IonRow>
              <IonRow>
              <IonCol key={3} size="2" style={{textAlign: 'center'}}>
              <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/icona-idrometri-3abf26.svg`} className="dock-legend-icon"></IonIcon>
                </IonCol>
                <IonCol key={4} size="10">
                  {t.hydrometers}
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol key={5} size="2" style={{paddingLeft: window.innerWidth > 768 ? '7%' : '6%'}}>
                  <div className={`dock-legend-icon-batimeter`}>
                    cm
                    <div className={`dock-legend-icon-batimetric-triangle`}></div>
                  </div>
                </IonCol>
                <IonCol key={6} size="10">
                  {t.bathymetricMonitoring}
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol key={7} size="2" style={{textAlign: 'center'}}>
                  <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/rimando-icon-ci-1.svg`} className="dock-legend-icon"></IonIcon>
                  <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/signs/boa-rossa-icon-ci-10.svg`} className="dock-legend-icon"></IonIcon>
                </IonCol>
                <IonCol key={8} size="10">
                  <IonRouterLink routerLink="/signs" style={{textDecoration: 'underline', color: 'black'}}>{t.signage}</IonRouterLink>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol key={9} size="2" style={{textAlign: 'center'}}>
                  <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/icona-allerta.svg`} className="dock-legend-icon"></IonIcon>
                </IonCol>
                <IonCol key={10} size="10">
                  {t.navigationWarnings}
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol key={11} size="2" style={{ textAlign: 'center', paddingTop: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 28,
                      height: 20,
                      background: 'rgba(255,0,0,0.35)',
                      border: '2px solid #ff0000',
                      borderRadius: 2,
                      verticalAlign: 'middle'
                    }}
                    aria-hidden
                  />
                </IonCol>
                <IonCol key={12} size="10">
                  {t.dangerZoneLegend}
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonAccordion>
        </IonAccordionGroup>

        {/* while loading */}
        {docksList == null &&
          <div>{loadingText}</div>
        }
        {/* if loading went fine */}
        {docksList != null && docksList.length > 0
          &&
          <>
            <div className="docks-map-wrap">
            <Map
              id="docksMap"
              ref={mapRef}
              onLoad={handleMapLoad}
              onZoom={(e) => handleMapZoom(e.viewState.zoom)}
              onRotate={(e) => handleRotationChange(e.viewState.bearing)} // Add this line
              onDragStart={handleMapDragStart}
              initialViewState={{
                latitude: 44.90831031864024,
                longitude: 10.557107582646871,
                zoom: 12,
                bearing: bearing, // Set the bearing based on user movement
                pitch: is3D ? 60 : 0, // Adjust pitch for 3D effect
              }}
              // style={{width: 800, height: 600}}
              mapStyle={mapStyle}
              mapboxAccessToken={MAPBOX_TOKEN}
              onStyleData={activateGeolocator}
            >
              {docksList.map((dock, index) => (
                <Marker key={index} longitude={dock.lng} latitude={dock.lat} onClick={() => {openPopupDock(index)}} >
                  <div className={`visible-marker-${index % 5 == 0 ? '5' : index % 3 == 0 ? '3' : index % 2 == 0 ? '2' : index % 7 == 0 ? '7' : 'other'}`}>
                    <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/map-marker.svg`} className="dock-legend-icon"></IonIcon>
                  </div>
                </Marker>
              ))
              }
              {signsList && signsList.map((sign, index) => (
                <Marker key={index} longitude={sign.point_lng} latitude={sign.point_lat} onClick={() => openSignPopup(index)} anchor="bottom" offset={[0, -20]} >
                  <div className={`visible-marker-${index % 5 == 0 ? '5' : index % 3 == 0 ? '3' : index % 2 == 0 ? '2' : index % 7 == 0 ? '7' : 'other'}`}>
                    {getSignIcon(sign)}
                  </div>
                </Marker>
              ))
              }
              {batimetricList && batimetricList.map((batimetric, index) => (
                <Marker key={index} longitude={batimetric.point_lng} latitude={batimetric.point_lat} anchor="bottom">
                  <div className='batimetric-name-container' translate="no">
                    <div className={`batimetric-name visible-marker-other`}>
                      {batimetric.extendedData_Nome_Local}
                    </div>
                    <div className={`batimetric-icon visible-marker-${index % 5 == 0 ? '5' : index % 3 == 0 ? '3' : index % 2 == 0 ? '2' : index % 7 == 0 ? '7' : 'other'}`}>
                      {!isNaN(batimetric.p) ? batimetric.p : ' NP'}
                      <div className={`batimetric-triangle`}></div>
                    </div>
                  </div>
                </Marker>
              ))
              }

              {/* deepness levels with the icon of different color depending on the level of the river [ranges taken from the json file station_color_mapping] */}
              {deepnessLevelsList && deepnessLevelsList.map((deepnessLevel, index) => {
                // Get the ranges for the current station
                const stationName = deepnessLevel.name;
                const stationRanges = (stationColorMapping as StationMapping)[stationName];
                let markerColor = '3abf26'; // Default color (gray) if no match is found

                if (stationRanges) {
                  // Find the matching color range
                  for (const [colorCode, range] of Object.entries(stationRanges)) {
                    const [min, max] = range;
                    if (deepnessLevel.value >= min && deepnessLevel.value <= max) {
                      markerColor = `${colorCode}`; // Use the hex color from the JSON
                      break;
                    }
                  }
                }
                return (
                  <Marker key={index} longitude={deepnessLevel.lng} latitude={deepnessLevel.lat} onClick={() => openSignPopup(index)} anchor="bottom">
                    <div className='batimetric-name-container' translate="no">
                      <div className={`deepness-name visible-marker-other`} style={{color: '#'+markerColor}}>
                        {deepnessLevel.name} {deepnessLevel.value} m
                      </div>
                      <div className={`deepness-icon`}>
                        <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/icona-idrometri-${markerColor}.svg`} className="dock-legend-icon" ></IonIcon>
                      </div>
                    </div>
                  </Marker>
                );
              })}

              {warningsList && warningsList.map((warning, index) => {
                const wz = warning as unknown as { danger_zone_id?: number | string; lng?: number | string; lat?: number | string; title?: string };
                const hasDz = Number(wz.danger_zone_id) > 0;
                return (
                <React.Fragment key={index}>
                  {(Number(wz.lng) > 0 || hasDz) &&
                    <Marker key={index} longitude={Number(warning.lng)} latitude={Number(warning.lat)} onClick={() => {openPopupWarning(index)}} anchor="bottom">
                      <div className={hasDz ? 'danger-warning-marker-wrap' : undefined}>
                        {hasDz &&
                          <div className="danger-zone-map-label" translate="no">{warning.title}</div>
                        }
                        <IonIcon icon={`${process.env.PUBLIC_URL}/assets/images/icona-allerta.svg`} className="dock-legend-icon"></IonIcon>
                      </div>
                    </Marker>
                  }
                </React.Fragment>
              );})}

              {/* add popup on dock or warning */}
              {indexToUseDock !== null && docksList &&
                <Popup
                  latitude={docksList[indexToUseDock].lat}
                  longitude={docksList[indexToUseDock].lng}
                  onClose={closePopupDock}
                  closeButton={true}
                  closeOnClick={false}
                  offset={35}
                  className="mapPopup"
                >
                  <h3><IonRouterLink routerLink={`/dock/${indexToUseDock}`}>{docksList[indexToUseDock].name}</IonRouterLink></h3>
                  <p>{t.currentDistance} {thisDockDistance}</p>
                  
                </Popup>
              }
              {indexToUseWarning !== null && warningsList && 
                <Popup
                  latitude={warningsList[indexToUseWarning].lat}
                  longitude={warningsList[indexToUseWarning].lng}
                  onClose={closePopupWarning}
                  closeButton={true}
                  closeOnClick={false}
                  offset={35}
                  className="mapPopup"
                >
                  <h3>{warningsList[indexToUseWarning].title}</h3>
                  {(warningsList[indexToUseWarning] as unknown as { body?: string }).body &&
                    <p>{(warningsList[indexToUseWarning] as unknown as { body?: string }).body}</p>
                  }
                </Popup>
              }
              {/* add popup on sign */}
              {selectedSignIndex &&
                <Popup
                  latitude={signsList![selectedSignIndex].point_lat}
                  longitude={signsList![selectedSignIndex].point_lng}
                  onClose={closeSignPopup}
                  closeButton={true}
                  closeOnClick={false}
                  offset={35}
                  className="mapPopup"
                >
                  <h2>{selectedSignTitle}</h2>
                  {/* <p>{signsList![selectedSignIndex].tratto} - {signsList![selectedSignIndex].extendedData_Nome_Local}</p> */}
                  {/* <p>{signsList![selectedSignIndex].extendedData_Tipo}</p> */}
                  <p>{t.location} {signsList![selectedSignIndex].extendedData_Nome_Local}</p>
                  <p>{t.description} {signsList![selectedSignIndex].extendedData_descrizione}</p>
                  <p>{t.shore} {signsList![selectedSignIndex].shore}</p>
                  <p>{t.riverSection} {signsList![selectedSignIndex].tratto}</p>
                  <p>{t.riverZone} {signsList![selectedSignIndex].presidio}</p>
                  <p>{t.id} {signsList![selectedSignIndex].id}</p>
                </Popup>
              }

              {userCoords &&
                <Marker
                  latitude={userCoords.latitude}
                  longitude={userCoords.longitude}
                  anchor="center"
                >
                  <div className="user-position-dot" aria-hidden="true"></div>
                </Marker>
              }
              <Source
                id="openseamap"
                type="raster"
                tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']}
                tileSize={256}
              />
              <Layer
                id="openseamap-layer"
                type="raster"
                source="openseamap"
              />

              {dangerZonesGeoJson && (
                <Source id="danger-zones" type="geojson" data={dangerZonesGeoJson}>
                  <Layer
                    id="danger-zones-fill"
                    type="fill"
                    paint={{
                      'fill-color': '#ff0000',
                      'fill-opacity': 0.35
                    }}
                  />
                  <Layer
                    id="danger-zones-outline"
                    type="line"
                    paint={{
                      'line-color': '#ff0000',
                      'line-width': 3
                    }}
                  />
                </Source>
              )}

            </Map>
            <div className="map-recenter-control">
              <button
                type="button"
                className={`map-recenter-btn${trackUserLocation ? ' map-recenter-btn--active' : ''}`}
                onClick={handleRecenterOnUser}
                aria-label={t.recenterOnUser}
                title={t.recenterOnUser}
              >
                <IonIcon icon={locateOutline} />
              </button>
            </div>
            </div>
            {/* Disclaimer Modal */}
            <IonModal
              isOpen={showModal}
              backdropDismiss={false}
              onDidDismiss={() => checkDisclamerCheckbox()}>
              {/* custom content created to show the big text and the disclaimer checkbox - passing to the child the method to trigger the closing of the modal */}
              <Disclaimer setDisclaimerChecked={setCheckbox} />
            </IonModal>
          </>
        }
      </IonContent>
      <IonAlert
        header={t.errorHeader}
        isOpen={showAlert2}
        message={t.errorMessage}
        buttons={[{ text: t.okButton }]}
        onDidDismiss={() => setShowAlert2(false)}
      />
      <GpsSinglePointComponent setUserCoords={setUserCoords} /> {/* pass the setter function to the child so we maintain the state watcher */}
      <PageEventHandler pageIndex={1} dockIndex={0} />
      <NotificationRedirect /> {/* redirect to the page specified in the local storage */}
    </IonPage>
  );
};

export default DocksMap;
